import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { advanceReceiptAPI, customerAPI, broadbandAPI, catvAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { format } from 'date-fns';
import toast from 'react-hot-toast';
import { Receipt, Plus, X, FileText, Send, Link2, Ban, Search, Phone, ChevronDown, ChevronUp, CalendarDays } from 'lucide-react';
import { useViewMode } from '../hooks/useViewMode';
import '../components/common/appView.css';
import PaymentMethodSelect from '../components/common/PaymentMethodSelect';
import AddableSelect from '../components/common/AddableSelect';
import useDarkReady from '../theme/useDarkReady';

// Payments → Advance Receipts (2026-09-17). Money collected before a connection is done:
// the person isn't a customer yet, so they get an ADV<n> receipt on WhatsApp as proof of
// payment. Once the connection is done and the customer is added, "Apply to customer"
// credits it through the normal payment flow so it's adjusted in the first bill
// (see backend services/advanceReceiptService).

const fmt = (n) => new Intl.NumberFormat('en-IN').format(Math.round(n || 0));
const SERVICE_LABELS = { catv: 'Cable TV', broadband: 'Broadband', both: 'Cable TV + Broadband' };
const STATUS_STYLE = {
  pending: { label: 'Connection pending', color: 'var(--c-b45309)', bg: 'var(--cb-fef3c7)' },
  applied: { label: 'Applied', color: 'var(--c-15803d)', bg: 'var(--cb-dcfce7)' },
  cancelled: { label: 'Cancelled', color: 'var(--c-475569)', bg: 'var(--cb-e2e8f0)' },
};

const EMPTY_FORM = {
  name: '', mobile: '', address: '', houseNumber: '', society: '', planName: '', packageName: '', serviceType: 'catv', amount: '',
  paymentMethod: 'cash', transactionId: '', notes: '', paymentDate: format(new Date(), 'yyyy-MM-dd'), sendWhatsapp: true,
};

function openPdf(id) {
  // Opened before the fetch so the browser doesn't treat it as a blocked pop-up.
  const win = window.open('', '_blank');
  advanceReceiptAPI.getPdf(id)
    .then(({ data }) => {
      const url = URL.createObjectURL(data);
      if (win) win.location = url; else window.location.href = url;
    })
    .catch(() => { if (win) win.close(); toast.error('Failed to load receipt'); });
}

function NewAdvanceModal({ onClose, onCreated, app }) {
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const hasCatv = form.serviceType !== 'broadband';
  const hasBroadband = form.serviceType !== 'catv';

  // Active plans/packages for the pickers. null = couldn't load (no Plans view right), in
  // which case the field falls back to typing the name.
  const [plans, setPlans] = useState([]);
  const [packages, setPackages] = useState([]);
  const [societies, setSocieties] = useState([]);
  useEffect(() => {
    broadbandAPI.getPlans().then(({ data }) => setPlans(data.data || [])).catch(() => setPlans(null));
    catvAPI.getPackages().then(({ data }) => setPackages(data.data || [])).catch(() => setPackages(null));
    // Societies already used by CableTV customers, merged into the admin-curated list.
    customerAPI.getAreas({ customerType: 'catv,both' }).then(({ data }) => setSocieties(data.data || [])).catch(() => {});
  }, []);

  const planField = (label, key, list, optionLabel) => (
    <div className="form-group" style={{ margin: 0 }}>
      <label className="form-label">{label} *</label>
      {list === null ? (
        <input className="form-control" value={form[key]} onChange={(e) => set(key, e.target.value)} required />
      ) : (
        <select className="form-control" value={form[key]} onChange={(e) => set(key, e.target.value)} required>
          <option value="">Select…</option>
          {list.map((p) => <option key={p._id} value={p.displayName || p.name}>{optionLabel(p)}</option>)}
        </select>
      )}
    </div>
  );

  const submit = async (e) => {
    e.preventDefault();
    if (hasCatv && (!form.houseNumber.trim() || !form.society.trim())) { toast.error('Enter the house number and society name'); return; }
    if (!/^\d{10}$/.test(form.mobile)) { toast.error('Enter a valid 10-digit mobile number'); return; }
    if (!(Number(form.amount) > 0)) { toast.error('Enter the amount received'); return; }
    setSaving(true);
    try {
      const { data } = await advanceReceiptAPI.create({ ...form, amount: Number(form.amount) });
      const { advance, whatsapp } = data.data;
      if (whatsapp.sent) toast.success(`${advance.receiptNumber} created and sent on WhatsApp`);
      else if (whatsapp.skipped) toast.success(`${advance.receiptNumber} created`);
      else toast.error(`${advance.receiptNumber} created, but WhatsApp failed: ${whatsapp.error || 'unknown error'}`);
      onCreated(advance);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to create advance receipt');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className={`modal${app ? ' adv-sheet' : ''}`} style={{ maxWidth: 560, width: '95vw' }} onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>New Advance Receipt</h3>
          <button className="modal-close" onClick={onClose}><X size={16} /></button>
        </div>
        <form onSubmit={submit}>
          <div className="modal-body" style={{ display: 'grid', gap: 12 }}>
            {app && <div className="adv-section">Customer</div>}
            <div className="form-grid">
              <div className="form-group" style={{ margin: 0 }}>
                <label className="form-label">Name *</label>
                <input className="form-control" value={form.name} onChange={(e) => set('name', e.target.value)} required autoFocus />
              </div>
              <div className="form-group" style={{ margin: 0 }}>
                <label className="form-label">Mobile (WhatsApp) *</label>
                <input className="form-control" value={form.mobile} inputMode="numeric" maxLength={10} autoComplete="tel"
                  onChange={(e) => set('mobile', e.target.value.replace(/\D/g, '').slice(0, 10))} required />
              </div>
            </div>
            {app && <div className="adv-section">Connection</div>}
            <div className="form-grid">
              <div className="form-group" style={{ margin: 0 }}>
                <label className="form-label">Connection *</label>
                <select className="form-control" value={form.serviceType} onChange={(e) => set('serviceType', e.target.value)}>
                  {Object.entries(SERVICE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
              </div>
              {hasBroadband && planField('Broadband plan', 'planName', plans, (p) => `${p.displayName || p.name} — ₹${fmt(p.price)}`)}
              {!hasBroadband && planField('Cable TV package', 'packageName', packages, (p) => `${p.displayName || p.name} — ₹${fmt(p.price)}`)}
            </div>
            {form.serviceType === 'both' && planField('Cable TV package', 'packageName', packages, (p) => `${p.displayName || p.name} — ₹${fmt(p.price)}`)}
            {hasCatv ? (
              <div className="form-grid">
                <div className="form-group" style={{ margin: 0 }}>
                  <label className="form-label">House / Flat No. *</label>
                  <input className="form-control" value={form.houseNumber} onChange={(e) => set('houseNumber', e.target.value)} placeholder="e.g. A-101" required />
                </div>
                <div className="form-group" style={{ margin: 0 }}>
                  <label className="form-label">Society name *</label>
                  <AddableSelect optionsKey="societies_catv" value={form.society} onChange={(v) => set('society', v)} placeholder="Select society…" extraOptions={societies} searchable />
                </div>
              </div>
            ) : (
              <div className="form-group" style={{ margin: 0 }}>
                <label className="form-label">Address</label>
                <input className="form-control" value={form.address} onChange={(e) => set('address', e.target.value)} />
              </div>
            )}
            {app && <div className="adv-section">Payment</div>}
            <div className="form-grid">
              <div className="form-group" style={{ margin: 0 }}>
                <label className="form-label">Amount received (₹) *</label>
                <input className="form-control" type="number" inputMode="numeric" min="1" step="1" value={form.amount} onChange={(e) => set('amount', e.target.value)} required />
              </div>
              <div className="form-group" style={{ margin: 0 }}>
                <label className="form-label">Payment method *</label>
                <PaymentMethodSelect className="form-control" value={form.paymentMethod} onChange={(v) => set('paymentMethod', v)} />
              </div>
            </div>
            <div className="form-group" style={{ margin: 0 }}>
              <label className="form-label">Payment date</label>
              <input className="form-control" type="date" max={format(new Date(), 'yyyy-MM-dd')} value={form.paymentDate} onChange={(e) => set('paymentDate', e.target.value)} />
            </div>
            {form.paymentMethod !== 'cash' && (
              <div className="form-group" style={{ margin: 0 }}>
                <label className="form-label">Transaction / reference no.</label>
                <input className="form-control" value={form.transactionId} onChange={(e) => set('transactionId', e.target.value)} />
              </div>
            )}
            <div className="form-group" style={{ margin: 0 }}>
              <label className="form-label">Notes</label>
              <input className="form-control" value={form.notes} onChange={(e) => set('notes', e.target.value)} />
            </div>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '.85rem', cursor: 'pointer' }}>
              <input type="checkbox" checked={form.sendWhatsapp} onChange={(e) => set('sendWhatsapp', e.target.checked)} />
              Send the receipt on WhatsApp now
            </label>
            <div style={{ fontSize: '.78rem', color: 'var(--text-muted)' }}>
              No customer balance changes yet. When the connection is done, use "Apply to customer" to adjust this amount in their first bill.
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>Close</button>
            <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? 'Saving…' : Number(form.amount) > 0 ? `Create receipt · ₹${fmt(form.amount)}` : 'Create receipt'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function ApplyModal({ advance, onClose, onApplied, app }) {
  const [query, setQuery] = useState(advance.mobile);
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [picked, setPicked] = useState(null);
  const [saving, setSaving] = useState(false);
  const [confirming, setConfirming] = useState(false);

  const runSearch = useCallback(async (q) => {
    if (!q.trim()) { setResults([]); return; }
    setSearching(true);
    try {
      setResults(await customerAPI.search(q.trim(), 10));
    } catch {
      toast.error('Customer search failed');
    } finally {
      setSearching(false);
    }
  }, []);

  useEffect(() => { runSearch(advance.mobile); }, [advance.mobile, runSearch]);

  const submit = async () => {
    if (!picked) return;
    setSaving(true);
    try {
      const { data } = await advanceReceiptAPI.apply(advance._id, picked._id);
      toast.success(`Applied — payment ${data.data.payment.receiptNumber} recorded on ${picked.name}`);
      onApplied();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to apply');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className={`modal${app ? ' adv-sheet' : ''}`} style={{ maxWidth: 560, width: '95vw' }} onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Apply {advance.receiptNumber} to customer</h3>
          <button className="modal-close" onClick={onClose}><X size={16} /></button>
        </div>
        <div className="modal-body" style={{ display: 'grid', gap: 12 }}>
          <div style={{ fontSize: '.85rem' }}>
            <b>₹{fmt(advance.amount)}</b> from <b>{advance.name}</b> ({advance.mobile}), collected {format(new Date(advance.paymentDate), 'dd MMM yyyy')}.
            It's credited to the customer's balance and adjusted in their bill.
          </div>
          <form onSubmit={(e) => { e.preventDefault(); runSearch(query); }} style={{ display: 'flex', gap: 8 }}>
            <input className="form-control" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search name, mobile, account no." />
            <button type="submit" className="btn btn-secondary btn-sm" style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}><Search size={13} /> Search</button>
          </form>
          <div style={{ maxHeight: 280, overflowY: 'auto', border: '1px solid var(--border)', borderRadius: 8 }}>
            {searching ? <div style={{ padding: 12, color: 'var(--text-muted)', fontSize: '.85rem' }}>Searching…</div>
              : results.length === 0 ? <div style={{ padding: 12, color: 'var(--text-muted)', fontSize: '.85rem' }}>No customers found. Add the customer first, then apply.</div>
                : results.map((c) => (
                  <label key={c._id} style={{ display: 'flex', gap: 10, alignItems: 'center', padding: '8px 12px', borderBottom: '1px solid var(--border)', cursor: 'pointer', background: picked?._id === c._id ? 'var(--primary-light, #eff6ff)' : undefined }}>
                    <input type="radio" name="apply-customer" checked={picked?._id === c._id} onChange={() => { setPicked(c); setConfirming(false); }} />
                    <div style={{ fontSize: '.85rem' }}>
                      <div style={{ fontWeight: 600 }}>{c.name} <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>· {c.customerId}</span></div>
                      <div style={{ color: 'var(--text-muted)' }}>
                        {c.mobile}{c.catv?.accountNumber ? ` · A/c ${c.catv.accountNumber}` : ''}{c.broadband?.username ? ` · ${c.broadband.username}` : ''} · Balance ₹{fmt(c.outstandingBalance)}
                      </div>
                    </div>
                  </label>
                ))}
          </div>
        </div>
        {confirming && picked && (
          <div className="adv-confirm">
            Credit <b>₹{fmt(advance.amount)}</b> ({advance.receiptNumber}) to <b>{picked.name}</b>? This records a payment on their account and cannot be applied again.
          </div>
        )}
        <div className="modal-footer">
          {confirming
            ? <button type="button" className="btn btn-secondary" disabled={saving} onClick={() => setConfirming(false)}>Back</button>
            : <button type="button" className="btn btn-secondary" onClick={onClose}>Close</button>}
          <button type="button" className="btn btn-primary" disabled={!picked || saving} onClick={confirming ? submit : () => setConfirming(true)}>
            {saving ? 'Applying…' : confirming ? 'Yes, apply advance' : 'Apply advance'}
          </button>
        </div>
      </div>
    </div>
  );
}

function CancelModal({ advance, onClose, onCancelled, app }) {
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await advanceReceiptAPI.cancel(advance._id, reason);
      toast.success(`${advance.receiptNumber} cancelled`);
      onCancelled();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to cancel');
    } finally {
      setSaving(false);
    }
  };
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className={`modal${app ? ' adv-sheet' : ''}`} style={{ maxWidth: 440, width: '95vw' }} onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Cancel {advance.receiptNumber}</h3>
          <button className="modal-close" onClick={onClose}><X size={16} /></button>
        </div>
        <form onSubmit={submit}>
          <div className="modal-body" style={{ display: 'grid', gap: 10 }}>
            <div style={{ fontSize: '.85rem' }}>Use this if the money was refunded or the receipt was made by mistake. It doesn't affect any customer balance.</div>
            <input className="form-control" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason (e.g. refunded, connection not possible)" required autoFocus />
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>Close</button>
            <button type="submit" className="btn btn-danger" disabled={saving}>{saving ? 'Cancelling…' : 'Cancel receipt'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

const STATUS_TABS = [['pending', 'Pending'], ['applied', 'Applied'], ['cancelled', 'Cancelled'], ['', 'All']];

// Phone card: summary always visible, tap for the rest + actions.
function AdvanceCard({ r, open, onToggle, canAdd, canShare, canCancel, sending, onResend, onApply, onCancel }) {
  const st = STATUS_STYLE[r.status];
  const where = [r.houseNumber, r.society].filter(Boolean).join(', ') || r.address;
  const row = (k, v) => v ? <div className="adv-kv"><span>{k}</span><span>{v}</span></div> : null;
  return (
    <div className="adv-card">
      <button type="button" className="adv-card-head" onClick={onToggle} aria-expanded={open}>
        <div className="adv-top">
          <span className="adv-no">{r.receiptNumber}</span>
          <span className="adv-badge" style={{ color: st.color, background: st.bg }}>{st.label}</span>
        </div>
        <div className="adv-mid">
          <div style={{ minWidth: 0 }}>
            <div className="adv-name">{r.name}</div>
            <div className="adv-sub">{r.mobile}{where ? ` · ${where}` : ''}</div>
          </div>
          <div className="adv-amt">₹{fmt(r.amount)}</div>
        </div>
        <div className="adv-sub adv-foot">
          <span>{format(new Date(r.paymentDate), 'dd MMM yyyy')} · {r.paymentMethod} · {SERVICE_LABELS[r.serviceType]}</span>
          {open ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
        </div>
        {r.status === 'applied' && r.customer && (
          <div className="adv-note" style={{ color: 'var(--c-15803d)' }}>Adjusted → {r.customer.name}{r.appliedPayment ? ` (${r.appliedPayment.receiptNumber})` : ''}</div>
        )}
        {r.status !== 'cancelled' && !r.whatsappSent && <div className="adv-note" style={{ color: 'var(--c-b91c1c)' }}>WhatsApp not sent</div>}
      </button>
      {open && (
        <div className="adv-detail">
          {row('Plan', r.planName)}
          {row('Package', r.packageName)}
          {row('Address', r.address !== where && r.address)}
          {row('Transaction ref', r.transactionId)}
          {row('Notes', r.notes)}
          {row('Collected by', r.collectedByName)}
          {row('Applied on', r.appliedAt && format(new Date(r.appliedAt), 'dd MMM yyyy, hh:mm a'))}
          {row('Cancel reason', r.status === 'cancelled' && r.cancelReason)}
          {row('WhatsApp', r.status !== 'cancelled' && !r.whatsappSent && r.whatsappError)}
          <div className="adv-actions">
            <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => openPdf(r._id)}><FileText size={14} /> Receipt</button>
            <a className="btn btn-sm btn-outline-secondary" href={`tel:${r.mobile}`}><Phone size={14} /> Call</a>
            {canShare && r.status !== 'cancelled' && (
              <button type="button" className="btn btn-sm btn-outline-secondary" disabled={sending} onClick={onResend}><Send size={14} /> {sending ? 'Sending…' : 'WhatsApp'}</button>
            )}
            {canCancel && r.status === 'pending' && (
              <button type="button" className="btn btn-sm btn-outline-secondary" style={{ color: 'var(--c-b91c1c)' }} onClick={onCancel}><Ban size={14} /> Cancel</button>
            )}
            {canAdd && r.status === 'pending' && (
              <button type="button" className="btn btn-sm btn-primary adv-wide" onClick={onApply}><Link2 size={14} /> Apply to customer</button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default function AdvanceReceiptsPage() {
  useDarkReady();
  const { viewMode } = useViewMode();
  const app = viewMode === 'app' || window.innerWidth < 640;
  const [openId, setOpenId] = useState(null);
  const [showDates, setShowDates] = useState(false);
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const { hasModuleRight } = useAuth();
  const canAdd = hasModuleRight('payments', 'add');
  const canShare = hasModuleRight('payments', 'shareReceipt');
  const canCancel = hasModuleRight('payments', 'delete');

  const [rows, setRows] = useState([]);
  const [pagination, setPagination] = useState({ total: 0, page: 1, pages: 1 });
  const [status, setStatus] = useState('pending');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  // ?new=1 (the dashboard's "Advance Receipt" button) opens the collect form straight away.
  const [searchParams] = useSearchParams();
  const [showNew, setShowNew] = useState(() => canAdd && searchParams.get('new') === '1');
  const [applying, setApplying] = useState(null);
  const [cancelling, setCancelling] = useState(null);
  const [sendingId, setSendingId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await advanceReceiptAPI.getAll({ status: status || undefined, search: search || undefined, from: from || undefined, to: to || undefined, page, limit: 25 });
      setRows(data.data || []);
      setPagination(data.pagination || { total: 0, page: 1, pages: 1 });
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to load advance receipts');
    } finally {
      setLoading(false);
    }
  }, [status, search, from, to, page]);

  useEffect(() => { load(); }, [load]);

  const resend = async (r) => {
    setSendingId(r._id);
    try {
      await advanceReceiptAPI.resendWhatsapp(r._id);
      toast.success('Receipt sent on WhatsApp');
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'WhatsApp send failed');
    } finally {
      setSendingId(null);
    }
  };

  const modals = (
    <>
      {showNew && <NewAdvanceModal app={app} onClose={() => setShowNew(false)} onCreated={() => { setShowNew(false); setStatus('pending'); setPage(1); load(); }} />}
      {applying && <ApplyModal app={app} advance={applying} onClose={() => setApplying(null)} onApplied={() => { setApplying(null); load(); }} />}
      {cancelling && <CancelModal app={app} advance={cancelling} onClose={() => setCancelling(null)} onCancelled={() => { setCancelling(null); load(); }} />}
    </>
  );

  if (app) {
    const pageTotal = rows.reduce((t, r) => t + (r.amount || 0), 0);
    return (
      <div className="adv-app">
        <div className="adv-bar">
          <div className="adv-search">
            <Search size={16} />
            <input className="form-control" type="search" value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              placeholder="Name, mobile, ADV no., amount, ref…" />
            <button type="button" className={`adv-iconbtn${from || to ? ' on' : ''}`} onClick={() => setShowDates((v) => !v)} aria-label="Filter by date"><CalendarDays size={18} /></button>
          </div>
          {showDates && (
            <div className="adv-dates">
              <label>From<input className="form-control" type="date" value={from} max={to || undefined} onChange={(e) => { setFrom(e.target.value); setPage(1); }} /></label>
              <label>To<input className="form-control" type="date" value={to} min={from || undefined} onChange={(e) => { setTo(e.target.value); setPage(1); }} /></label>
              {(from || to) && <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => { setFrom(''); setTo(''); setPage(1); }}>Clear</button>}
            </div>
          )}
          <div className="adv-tabs" role="tablist">
            {STATUS_TABS.map(([k, label]) => (
              <button key={k || 'all'} type="button" role="tab" aria-selected={status === k} className={status === k ? 'on' : ''}
                onClick={() => { setStatus(k); setPage(1); setOpenId(null); }}>{label}</button>
            ))}
          </div>
        </div>

        <div className="adv-summary">
          {loading && rows.length === 0 ? 'Loading…' : `${pagination.total} receipt${pagination.total === 1 ? '' : 's'}${rows.length ? ` · ₹${fmt(pageTotal)} on this page` : ''}`}
        </div>

        {rows.length === 0 && !loading ? (
          <div className="adv-empty">No advance receipts{search || from || to ? ' match these filters' : ''}.</div>
        ) : rows.map((r) => (
          <AdvanceCard key={r._id} r={r} open={openId === r._id} onToggle={() => setOpenId(openId === r._id ? null : r._id)}
            canAdd={canAdd} canShare={canShare} canCancel={canCancel} sending={sendingId === r._id}
            onResend={() => resend(r)} onApply={() => setApplying(r)} onCancel={() => setCancelling(r)} />
        ))}

        {pagination.pages > 1 && (
          <div className="adv-pager">
            <button type="button" className="btn btn-sm btn-outline-secondary" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>Previous</button>
            <span>{pagination.page} / {pagination.pages}</span>
            <button type="button" className="btn btn-sm btn-outline-secondary" disabled={page >= pagination.pages} onClick={() => setPage((p) => p + 1)}>Next</button>
          </div>
        )}

        {canAdd && <button type="button" className="adv-fab" onClick={() => setShowNew(true)}><Plus size={18} /> New receipt</button>}
        {modals}
      </div>
    );
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10, marginBottom: 4 }}>
        <h2 style={{ margin: 0 }}><Receipt size={18} style={{ marginRight: 8, verticalAlign: -2 }} />Advance Receipts</h2>
        {canAdd && (
          <button type="button" className="btn btn-primary btn-sm" onClick={() => setShowNew(true)} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <Plus size={14} /> New advance receipt
          </button>
        )}
      </div>
      <div style={{ color: 'var(--text-muted)', fontSize: '.85rem', marginBottom: 18 }}>
        Payment collected before the connection is done. The customer gets a receipt on WhatsApp as proof of payment;
        once they're added as a customer, apply it so the amount is adjusted in their first bill.
      </div>

      <div className="card">
        <div className="card-body">
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginBottom: 14 }}>
            <select className="form-control" value={status} onChange={(e) => { setStatus(e.target.value); setPage(1); }} style={{ width: 200 }}>
              <option value="">All</option>
              {Object.entries(STATUS_STYLE).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
            </select>
            <input className="form-control" value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              placeholder="Search name, mobile, society, ADV no." style={{ width: 260 }} />
          </div>

          {loading && rows.length === 0 ? (
            <div style={{ color: 'var(--text-muted)', fontSize: '.85rem' }}>Loading…</div>
          ) : rows.length === 0 ? (
            <div style={{ color: 'var(--text-muted)', fontSize: '.85rem' }}>No advance receipts.</div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', fontSize: '.85rem', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ color: 'var(--text-muted)', textAlign: 'left' }}>
                    {['Receipt', 'Date', 'Name / Mobile', 'Connection', 'Amount', 'Mode', 'Status', ''].map((h) => (
                      <th key={h} style={{ padding: '6px 10px', fontWeight: 600, whiteSpace: 'nowrap' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => {
                    const st = STATUS_STYLE[r.status];
                    return (
                      <tr key={r._id} style={{ borderTop: '1px solid var(--border)', verticalAlign: 'top' }}>
                        <td style={{ padding: '8px 10px', fontFamily: 'monospace', fontWeight: 700 }}>{r.receiptNumber}</td>
                        <td style={{ padding: '8px 10px', whiteSpace: 'nowrap' }}>{format(new Date(r.paymentDate), 'dd MMM yyyy')}</td>
                        <td style={{ padding: '8px 10px' }}>
                          <div style={{ fontWeight: 600 }}>{r.name}</div>
                          <div style={{ color: 'var(--text-muted)' }}>{[r.mobile, [r.houseNumber, r.society].filter(Boolean).join(', '), r.address].filter(Boolean).join(' · ')}</div>
                          {r.collectedByName && <div style={{ color: 'var(--text-muted)', fontSize: '.75rem' }}>Collected by {r.collectedByName}</div>}
                        </td>
                        <td style={{ padding: '8px 10px' }}>
                          {SERVICE_LABELS[r.serviceType]}
                          {[r.planName, r.packageName].filter(Boolean).map((p) => <div key={p} style={{ color: 'var(--text-muted)', fontSize: '.78rem' }}>{p}</div>)}
                        </td>
                        <td style={{ padding: '8px 10px', fontWeight: 700, whiteSpace: 'nowrap' }}>₹{fmt(r.amount)}</td>
                        <td style={{ padding: '8px 10px' }}>{r.paymentMethod}{r.transactionId ? <div style={{ color: 'var(--text-muted)', fontSize: '.75rem' }}>{r.transactionId}</div> : null}</td>
                        <td style={{ padding: '8px 10px' }}>
                          <span style={{ fontSize: '.72rem', fontWeight: 700, color: st.color, background: st.bg, borderRadius: 10, padding: '2px 8px', whiteSpace: 'nowrap' }}>{st.label}</span>
                          {r.status === 'applied' && r.customer && (
                            <div style={{ fontSize: '.75rem', marginTop: 4 }}>
                              → {r.customer.name}{r.appliedPayment ? ` (${r.appliedPayment.receiptNumber})` : ''}
                            </div>
                          )}
                          {r.status === 'cancelled' && r.cancelReason && <div style={{ fontSize: '.75rem', marginTop: 4, color: 'var(--text-muted)' }}>{r.cancelReason}</div>}
                          {r.status !== 'cancelled' && !r.whatsappSent && (
                            <div style={{ fontSize: '.72rem', marginTop: 4, color: 'var(--c-b91c1c)' }} title={r.whatsappError || ''}>WhatsApp not sent</div>
                          )}
                        </td>
                        <td style={{ padding: '8px 0 8px 10px' }}>
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, justifyContent: 'flex-end' }}>
                            <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => openPdf(r._id)} title="View / print receipt"
                              style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}><FileText size={12} /> PDF</button>
                            {canShare && r.status !== 'cancelled' && (
                              <button type="button" className="btn btn-sm btn-outline-secondary" disabled={sendingId === r._id} onClick={() => resend(r)}
                                style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}><Send size={12} /> {sendingId === r._id ? 'Sending…' : 'WhatsApp'}</button>
                            )}
                            {canAdd && r.status === 'pending' && (
                              <button type="button" className="btn btn-sm btn-primary" onClick={() => setApplying(r)}
                                style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}><Link2 size={12} /> Apply to customer</button>
                            )}
                            {canCancel && r.status === 'pending' && (
                              <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => setCancelling(r)}
                                style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: 'var(--c-b91c1c)' }}><Ban size={12} /> Cancel</button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {pagination.pages > 1 && (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 12, marginTop: 16, fontSize: '.85rem' }}>
              <button type="button" className="btn btn-sm btn-outline-secondary" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>Previous</button>
              <span style={{ color: 'var(--text-muted)' }}>Page {pagination.page} of {pagination.pages} ({pagination.total} total)</span>
              <button type="button" className="btn btn-sm btn-outline-secondary" disabled={page >= pagination.pages} onClick={() => setPage((p) => p + 1)}>Next</button>
            </div>
          )}
        </div>
      </div>

      {modals}
    </div>
  );
}
